import { motion, AnimatePresence } from 'framer-motion';

const LOGO = "https://ibieannzbpwoamcowznu.supabase.co/storage/v1/object/public/email-assets/exora-logo-transparent.png?v=2";

interface ReactivationModalProps {
  open: boolean;
  firstName: string;
  onConfirm: () => void;
}

export default function ReactivationModal({ open, firstName, onConfirm }: ReactivationModalProps) {
  if (!open) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
          style={{ background: 'hsla(0 0% 3% / 0.85)', backdropFilter: 'blur(12px)' }}
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-md"
            style={{
              background: 'hsl(var(--surface-2))',
              border: '1px solid hsl(var(--gold) / 0.15)',
              borderRadius: '20px',
              overflow: 'hidden',
            }}
          >
            {/* Gold accent line */}
            <div style={{ height: '2px', background: 'linear-gradient(90deg, transparent, hsl(var(--gold) / 0.6), transparent)' }} />

            <div className="px-8 pt-10 pb-8 text-center">
              {/* Logo */}
              <img src={LOGO} alt="Exora" className="h-8 mx-auto mb-8 opacity-30" />

              {/* Label */}
              <div className="flex items-center justify-center gap-3 mb-6">
                <div style={{ height: '1px', width: '40px', background: 'hsl(var(--gold) / 0.15)' }} />
                <span style={{ fontSize: '8px', letterSpacing: '0.3em', color: 'hsl(var(--gold) / 0.6)', fontWeight: 500, textTransform: 'uppercase' }}>
                  ACCOUNT RESTORED
                </span>
                <div style={{ height: '1px', width: '40px', background: 'hsl(var(--gold) / 0.15)' }} />
              </div>

              {/* Heading */}
              <h2
                style={{
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  fontSize: '30px',
                  fontWeight: 300,
                  color: 'hsl(var(--ivory) / 0.9)',
                  lineHeight: 1.2,
                  marginBottom: '4px',
                }}
              >
                Welcome back,
              </h2>
              <h2
                style={{
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  fontSize: '30px',
                  fontWeight: 300,
                  fontStyle: 'italic',
                  color: 'hsl(var(--gold))',
                  lineHeight: 1.2,
                  marginBottom: '24px',
                }}
              >
                {firstName}.
              </h2>

              {/* Body */}
              <p
                style={{
                  fontSize: '13px',
                  color: 'hsl(var(--ivory) / 0.4)',
                  fontWeight: 300,
                  lineHeight: 1.8,
                  maxWidth: '320px',
                  margin: '0 auto 28px',
                }}
              >
                Your account deletion has been cancelled. All your bookings, profiles, and preferences are exactly where you left them.
              </p>

              {/* Info card */}
              <div
                className="text-left mx-auto mb-8"
                style={{
                  background: 'hsl(var(--background) / 0.5)',
                  border: '1px solid hsl(var(--gold) / 0.1)',
                  borderRadius: '12px',
                  padding: '16px 20px',
                  maxWidth: '340px',
                }}
              >
                <p style={{ fontSize: '9px', letterSpacing: '0.2em', color: 'hsl(var(--gold) / 0.5)', fontWeight: 500, textTransform: 'uppercase', marginBottom: '10px' }}>
                  WHAT'S BEEN PRESERVED
                </p>
                <div className="space-y-2">
                  {[
                    'Your booking history & confirmations',
                    'Saved properties & traveller profiles',
                    'Notification preferences & settings',
                  ].map((item) => (
                    <p key={item} style={{ fontSize: '11px', color: 'hsl(var(--ivory) / 0.35)', fontWeight: 300, lineHeight: 1.6 }}>
                      <span style={{ color: 'hsl(var(--gold) / 0.5)', marginRight: '8px' }}>✦</span>
                      {item}
                    </p>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <button
                onClick={onConfirm}
                className="transition-all duration-200"
                style={{
                  fontSize: '10px',
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  fontWeight: 400,
                  padding: '13px 40px',
                  borderRadius: '10px',
                  background: 'hsl(var(--gold) / 0.1)',
                  border: '1px solid hsl(var(--gold) / 0.3)',
                  color: 'hsl(var(--gold))',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'hsl(var(--gold) / 0.15)';
                  e.currentTarget.style.borderColor = 'hsl(var(--gold) / 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'hsl(var(--gold) / 0.1)';
                  e.currentTarget.style.borderColor = 'hsl(var(--gold) / 0.3)';
                }}
              >
                Continue to Exora
              </button>

              {/* Subtle footer */}
              <div className="flex items-center justify-center gap-3 mt-8">
                <div style={{ height: '1px', width: '30px', background: 'hsl(var(--gold) / 0.08)' }} />
                <span style={{ fontSize: '7px', color: 'hsl(var(--gold) / 0.25)' }}>✦</span>
                <div style={{ height: '1px', width: '30px', background: 'hsl(var(--gold) / 0.08)' }} />
              </div>
              <p style={{ fontSize: '9px', color: 'hsl(var(--ivory) / 0.12)', fontWeight: 300, marginTop: '8px', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                Luxury stays. Private rates.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
